// Browser-side helper around Cognito Identity Pool's role-mapping
// API. Used by `app.roleAdmin.sync()` to wire Cognito User Pool group
// membership → per-group IAM role.
//
// The role-mapping rules are stored on the pool's `RoleMappings`
// dictionary; we only ever touch the mappings for this app's own
// `cognito-idp.{region}.amazonaws.com/{poolId}:{clientId}` provider
// keys — other keys (e.g. the platform pool under operator federation)
// are preserved as-is.

import {
  CognitoIdentityClient,
  GetIdentityPoolRolesCommand,
  SetIdentityPoolRolesCommand,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-cognito-identity@3.1054.0/+esm";

export interface IdentityPoolAdminConfig {
  region: string;
  /** Credentials resolver — called per request so STS refresh flows. */
  credentials: () => Promise<{
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken?: string;
  }>;
}

export interface IdentityPoolRoleRule {
  /** Cognito group name to match. */
  groupName: string;
  /** IAM role ARN to assign when the user belongs to the group. */
  roleArn: string;
}

export class IdentityPoolAdmin {
  private client: CognitoIdentityClient;

  constructor(cfg: IdentityPoolAdminConfig) {
    this.client = new CognitoIdentityClient({
      region: cfg.region,
      credentials: async () => cfg.credentials(),
    });
  }

  /** Replace the rules for the given provider keys on the Identity Pool's
   *  role mapping. Preserves rules on other provider keys (in case
   *  multiple User Pools feed the same Identity Pool — e.g. the platform
   *  pool's federated mapping must survive a sync).
   *
   *  Pass ONE key per app client (spa + server). Every client that signs in
   *  needs its own mapping: the console authenticates with the server
   *  client, and a token from a client with no mapping falls back to the
   *  default authenticated role (so an owner would silently get the member
   *  role). The two kinds get DIFFERENT ambiguous-resolution: the spa key
   *  falls back to the authenticated (member) role, while server-client
   *  keys are `Deny`-resolved — only owner/admin (or app-role) rule matches
   *  get console credentials. This mirrors provisioning and the CLI; a
   *  single AuthenticatedRole mapping for both used to quietly re-open the
   *  console to plain members on every panel sync. */
  async putRoleMappingRules(args: {
    identityPoolId: string;
    /** SPA-client keys (`cognito-idp.{region}.amazonaws.com/{poolId}:{clientId}`)
     *  → ambiguous tokens fall back to the AuthenticatedRole (member). */
    spaProviderKeys: string[];
    /** SERVER-client keys → `Deny`: no matching rule, no credentials. */
    serverProviderKeys: string[];
    /** Default role when no rule matches; usually the slug-default
     *  authenticated role. */
    authenticatedRoleArn: string;
    /** The `unauthenticated` (visitor) role, when the pool allows anonymous
     *  identities. Omitted = leave the existing value untouched. */
    unauthenticatedRoleArn?: string;
    rules: IdentityPoolRoleRule[];
  }): Promise<void> {
    let existing: {
      Roles?: Record<string, string>;
      RoleMappings?: Record<string, unknown>;
    } = {};
    try {
      const got = await this.client.send(
        new GetIdentityPoolRolesCommand({ IdentityPoolId: args.identityPoolId }),
      );
      existing = {
        Roles: got.Roles,
        RoleMappings: got.RoleMappings as Record<string, unknown> | undefined,
      };
    } catch {
      // First-time setup.
    }

    const ruleList = args.rules.map((r) => ({
      Claim: "cognito:groups",
      MatchType: "Contains",
      Value: r.groupName,
      RoleARN: r.roleArn,
    }));
    const spaMapping = {
      Type: "Rules",
      AmbiguousRoleResolution: "AuthenticatedRole",
      RulesConfiguration: { Rules: ruleList },
    };
    const serverMapping = {
      Type: "Rules",
      AmbiguousRoleResolution: "Deny",
      RulesConfiguration: { Rules: ruleList },
    };
    const mappings = { ...(existing.RoleMappings ?? {}) };
    if (ruleList.length === 0) {
      // Cognito rejects an empty Rules array. No rules = no mapping: tokens
      // on these providers fall back to the AuthenticatedRole (member),
      // which is exactly what a rule-less project wants. Delete any stale
      // mapping so a removed last-rule actually heals.
      for (const key of [...args.spaProviderKeys, ...args.serverProviderKeys])
        delete mappings[key];
    } else {
      for (const key of args.spaProviderKeys) mappings[key] = spaMapping;
      for (const key of args.serverProviderKeys) mappings[key] = serverMapping;
    }

    await this.client.send(
      new SetIdentityPoolRolesCommand({
        IdentityPoolId: args.identityPoolId,
        Roles: {
          ...(existing.Roles ?? {}),
          authenticated: args.authenticatedRoleArn,
          ...(args.unauthenticatedRoleArn ? { unauthenticated: args.unauthenticatedRoleArn } : {}),
        },
        RoleMappings: mappings as never,
      }),
    );
  }
}
